
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, limitToLast, query } from 'firebase/database';
import { db } from '../firebase';
import { Product, Announcement } from '../types';
import { ChevronRight, Heart, ShieldCheck, Truck, Sparkles, ImageIcon, X, ArrowRight, LayoutGrid } from 'lucide-react';
import { useApp } from '../App';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [activeAnnouncements, setActiveAnnouncements] = useState<Announcement[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const { categories, addToCart } = useApp();

  // Sadece görünür olması seçilen kategorileri filtrele
  const visibleCategories = categories.filter(c => c.isVisibleOnHome !== false);

  useEffect(() => {
    // Ürünler
    const productsRef = query(ref(db, 'products'), limitToLast(8));
    onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) setFeaturedProducts(Object.values(data));
    });

    // Duyurular
    const annRef = ref(db, 'announcements');
    onValue(annRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const now = Date.now();
        const active = Object.values(data).filter((ann: any) => 
          ann.isActive && (!ann.expiryDate || ann.expiryDate > now)
        ) as Announcement[];
        
        setActiveAnnouncements(active);

        const popupAnn = active.find(a => a.type === 'popup');
        const sessionDismissed = sessionStorage.getItem('popupDismissed');
        if (popupAnn && !sessionDismissed) {
          setTimeout(() => setShowPopup(true), 1500);
        }
      }
    });
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('popupDismissed', 'true');
  };

  const bannerAnn = activeAnnouncements.find(a => a.type === 'banner');
  const popupAnn = activeAnnouncements.find(a => a.type === 'popup');

  return (
    <div className="space-y-12 pb-20">
      {/* Duyuru Bandı */}
      {bannerAnn && (
        <div className="bg-amber-800 text-white py-3 px-4 text-center text-[10px] md:text-xs font-black uppercase tracking-[0.2em] relative overflow-hidden group">
          <Link to={bannerAnn.buttonLink || '#'} className="flex items-center justify-center space-x-4">
            <Sparkles size={14} className="animate-pulse" />
            <span>{bannerAnn.title} — {bannerAnn.description}</span>
            <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" />
          </Link>
          <div className="absolute inset-0 bg-white/5 pointer-events-none"></div>
        </div>
      )}

      {/* İnce ve Kompakt Kategoriler (Admin Tarafından Belirlenenler) */}
      {visibleCategories.length > 0 && (
        <section className="container mx-auto px-4 md:px-6">
          <div className="flex items-center space-x-2 mb-4">
            <LayoutGrid size={16} className="text-amber-800" />
            <h2 className="text-xs font-black text-stone-900 uppercase tracking-widest">Koleksiyonlar</h2>
          </div>
          
          <div className="flex items-center space-x-3 overflow-x-auto scrollbar-hide pb-2 animate-in fade-in slide-in-from-top-2 duration-300">
            {visibleCategories.map((cat) => (
              <Link 
                key={cat.id} 
                to={`/category/${cat.slug}`} 
                className="flex items-center space-x-3 bg-white border border-stone-100 rounded-full px-5 py-2.5 hover:border-amber-500 hover:shadow-md transition-all group whitespace-nowrap"
              >
                <span className="text-lg group-hover:scale-110 transition-transform">{cat.icon || "🏺"}</span>
                <span className="font-bold text-stone-800 group-hover:text-amber-800 transition-colors uppercase tracking-widest text-[10px]">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Hero Banner */}
      <section className="container mx-auto px-4 md:px-6">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#E9E1D4] min-h-[400px] md:min-h-[500px] flex items-center">
          <div className="relative z-10 px-6 md:px-20 max-w-2xl py-12 md:py-0">
            <div className="inline-flex items-center space-x-2 bg-white/50 backdrop-blur-md px-4 py-1.5 rounded-full text-amber-900 text-[10px] font-bold mb-6">
              <Sparkles size={14} />
              <span>Yeni Sezon Koleksiyonu</span>
            </div>
            <h1 className="text-4xl md:text-7xl font-serif font-bold text-stone-900 mb-6 leading-tight">
              Evinize <br /> Ruh Katın
            </h1>
            <p className="text-stone-600 text-base md:text-lg mb-8 md:10 leading-relaxed font-medium">
              El yapımı soya mumları, tasarım biblolar ve doğal tütsülerle yaşam alanınızı huzur dolu bir sığınağa dönüştürün.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/category/mumlar" className="px-8 md:px-10 py-3 md:py-4 bg-amber-800 text-white font-bold rounded-2xl hover:bg-amber-900 transition-all shadow-xl shadow-amber-900/20 text-sm">
                Mumları Keşfet
              </Link>
              <Link to="/about" className="px-8 md:px-10 py-3 md:py-4 bg-white text-stone-800 font-bold rounded-2xl border border-stone-200 hover:bg-stone-50 transition-all text-sm">
                Hikayemiz
              </Link>
            </div>
          </div>
          <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block bg-[url('https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format&fit=crop&q=80&w=1000')] bg-cover bg-center">
            <div className="absolute inset-0 bg-gradient-to-r from-[#E9E1D4] to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Giriş Popup Modalı */}
      {showPopup && popupAnn && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white w-full max-w-4xl rounded-[3rem] overflow-hidden flex flex-col md:flex-row relative shadow-[0_0_100px_rgba(0,0,0,0.3)] animate-in zoom-in duration-500">
            <button onClick={closePopup} className="absolute top-6 right-6 z-20 w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center text-stone-900 hover:bg-white transition-all shadow-lg">
              <X size={20} />
            </button>
            <div className="flex-1 h-64 md:h-auto overflow-hidden">
              <img src={popupAnn.image || 'https://images.unsplash.com/photo-1602872030219-cdebd52110cb?auto=format&fit=crop&q=80&w=1000'} className="w-full h-full object-cover" alt={popupAnn.title} />
            </div>
            <div className="flex-1 p-10 md:p-16 flex flex-col justify-center text-center md:text-left">
              <div className="inline-flex items-center space-x-2 text-amber-800 font-black text-[10px] uppercase tracking-[0.3em] mb-4">
                <Sparkles size={14} />
                <span>Özel Kampanya</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6 leading-tight">{popupAnn.title}</h2>
              <p className="text-stone-500 text-sm md:text-base leading-relaxed mb-10 font-medium italic">{popupAnn.description}</p>
              <div className="mt-auto">
                <Link 
                  to={popupAnn.buttonLink || '#'} 
                  onClick={closePopup}
                  className="inline-block bg-stone-900 text-white px-10 py-5 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-amber-800 transition-all shadow-xl shadow-stone-900/20 active:scale-95"
                >
                  {popupAnn.buttonText || 'Keşfetmeye Başla'}
                </Link>
                <button onClick={closePopup} className="block mt-6 text-[10px] font-black text-stone-300 uppercase tracking-widest hover:text-stone-500 transition-colors mx-auto md:mx-0">Kapat ve Devam Et</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* En Çok Sevilenler */}
      <section className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">En Çok Sevilenler</h2>
          <Link to="/category/tum-urunler" className="text-amber-800 font-bold flex items-center hover:underline text-xs uppercase tracking-wider">
            Tümünü Gör <ChevronRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featuredProducts.map((p) => (
            <div key={p.id} className="group bg-white rounded-[2.5rem] border border-stone-100 overflow-hidden hover:shadow-2xl transition-all flex flex-col">
              <Link to={`/product/${p.id}`} className="block relative aspect-[4/5] bg-[#F9F7F4] overflow-hidden">
                {p.isNew && (
                  <span className="absolute top-6 left-6 z-10 bg-amber-800 text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">Yeni</span>
                )}
                <img 
                  src={p.images && p.images.length > 0 ? p.images[0] : ''} 
                  alt={p.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                />
              </Link>
              <div className="p-8 flex-1 flex flex-col">
                <div className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">{p.brand || "Lumina Craft"}</div>
                <Link to={`/product/${p.id}`} className="font-serif font-bold text-lg text-stone-900 hover:text-amber-800 mb-3 line-clamp-1 transition-colors">
                  {p.name}
                </Link>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-xl font-black text-stone-900">₺{p.price?.toLocaleString() || 0}</span>
                  <button 
                    onClick={() => addToCart(p)}
                    className="px-5 py-2.5 bg-stone-900 text-white text-xs font-bold rounded-xl hover:bg-amber-800 transition-all active:scale-95"
                  >
                    Sepete Ekle
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Ribbon */}
      <section className="bg-[#FAF7F2] py-12 border-y border-stone-100">
        <div className="container mx-auto px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {[
            { icon: Sparkles, title: "Doğal İçerik", sub: "%100 Soya ve Balmumu" },
            { icon: Truck, title: "Hızlı Teslimat", sub: "Özenle Paketlenmiş" },
            { icon: ShieldCheck, title: "Güvenli Alışveriş", sub: "256-bit SSL Koruma" },
            { icon: Heart, title: "El Yapımı", sub: "Sevgiyle Tasarlandı" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-2xl flex items-center justify-center text-amber-700 shadow-sm border border-stone-50">
                <f.icon size={24} className="md:size-[28px]" />
              </div>
              <div>
                <h4 className="font-bold text-stone-900 text-xs">{f.title}</h4>
                <p className="text-stone-500 text-[10px] mt-1">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Marketing Section */}
      <section className="container mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-stone-900 rounded-[3rem] p-10 md:p-16 text-white flex flex-col justify-center items-start overflow-hidden relative group">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6 relative z-10">Özel Hediye <br /> Paketleme</h3>
            <p className="text-stone-400 mb-10 max-w-sm relative z-10 leading-relaxed font-medium text-sm md:text-base">Sevdiklerinize şık ve ekolojik paketleme seçeneklerimizle unutulmaz bir sürpriz yapın.</p>
            <button className="px-10 py-4 bg-amber-700 text-white font-bold rounded-2xl hover:bg-amber-600 transition-all relative z-10 shadow-lg shadow-amber-900/40 text-xs">Detaylar</button>
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl"></div>
          </div>
          <div className="bg-[#E9E1D4] rounded-[3rem] p-10 md:p-16 text-stone-900 flex flex-col justify-center items-start overflow-hidden relative group border border-stone-200">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6 relative z-10">Toplu Sipariş & <br /> Kurumsal</h3>
            <p className="text-stone-600 mb-10 max-w-sm relative z-10 leading-relaxed font-medium text-sm md:text-base">Düğün, nişan veya kurumsal etkinlikleriniz için özel tasarım mum ve biblo çözümleri.</p>
            <button className="px-10 py-4 bg-white text-stone-900 font-bold rounded-2xl border border-stone-200 hover:bg-stone-50 transition-all relative z-10 text-xs">Teklif Al</button>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-800/5 rounded-full -mb-20 -mr-20"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
